import React, { useState, useEffect, useCallback } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

/* ─── helpers ────────────────────────────────────────────────────────────── */
const getYouTubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
};

const resolveVideoUrl = (v) => {
    const ytId = getYouTubeId(v.youtube_link) || getYouTubeId(v.video_path);
    if (ytId) return { type: 'youtube', ytId };
    const src = v.video_path || v.input_video;
    if (src) return { type: 'local', src: src.startsWith('http') ? src : `${API_LINK}${src.startsWith('/') ? '' : '/'}${src}` };
    return null;
};

const resolveNoteUrl = (n) => {
    const p = n.file_path || n.notes_pdf;
    if (!p) return null;
    return p.startsWith('http') ? p : `${API_LINK}${p.startsWith('/') ? '' : '/'}${p}`;
};

/* ─── VideoPlayer ─────────────────────────────────────────────────────────── */
const VideoPlayer = ({ video }) => {
    const info = resolveVideoUrl(video);
    if (!info) return (
        <div style={styles.noMedia}>🚫 No media available for this video.</div>
    );
    if (info.type === 'youtube') return (
        <div style={styles.ytWrap}>
            <iframe
                src={`https://www.youtube.com/embed/${info.ytId}?autoplay=1&rel=0`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={styles.ytFrame}
            />
        </div>
    );
    return (
        <video controls autoPlay style={styles.videoEl} src={info.src}>
            Your browser does not support video playback.
        </video>
    );
};

/* ─── Component ───────────────────────────────────────────────────────────── */
const StudentElearning = ({ onNavigate, userInfo }) => {
    const [playlists, setPlaylists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedPlaylist, setSelectedPlaylist] = useState(null);
    const [playlistDetail, setPlaylistDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [playingVideo, setPlayingVideo] = useState(null);
    const [activeTab, setActiveTab] = useState('videos');
    const [toast, setToast] = useState(null);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const showToast = (msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchPlaylists = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_LINK}/api/elearning/student/playlists`, { headers });
            const r = await res.json();
            if (r.success) setPlaylists(r.data || []);
            else showToast(r.message || 'Failed to load playlists', 'error');
        } catch { showToast('Network error loading playlists', 'error'); }
        finally { setLoading(false); }
    }, []);

    const fetchPlaylistDetail = async (id) => {
        try {
            setDetailLoading(true);
            setPlaylistDetail(null);
            const res = await fetch(`${API_LINK}/api/elearning/student/playlists/${id}`, { headers });
            const r = await res.json();
            if (r.success) {
                setPlaylistDetail(r.data);
                if (r.data?.videos?.length) setPlayingVideo(r.data.videos[0]);
            } else showToast('Failed to load content', 'error');
        } catch { showToast('Network error', 'error'); }
        finally { setDetailLoading(false); }
    };

    useEffect(() => { fetchPlaylists(); }, [fetchPlaylists]);

    const selectPlaylist = (p) => {
        setSelectedPlaylist(p);
        setPlayingVideo(null);
        setActiveTab('videos');
        fetchPlaylistDetail(p.id);
    };

    const filtered = playlists.filter(p =>
        `${p.name} ${p.subject} ${p.department} ${p.teacher_name || ''}`.toLowerCase().includes(search.toLowerCase())
    );

    const groupedPlaylists = filtered.reduce((acc, p) => {
        const dep = p.department || 'General';
        if (!acc[dep]) acc[dep] = [];
        acc[dep].push(p);
        return acc;
    }, {});

    const totalVideos = playlists.reduce((s, p) => s + (p.video_count || 0), 0);
    const totalNotes  = playlists.reduce((s, p) => s + (p.note_count  || 0), 0);

    return (
        <Layout role="student" onNavigate={onNavigate} activePage="student-elearning" userInfo={userInfo}>
            <style>{CSS}</style>

            {/* Toast */}
            {toast && (
                <div className={`el-toast el-toast-${toast.type}`}>{toast.msg}</div>
            )}

            {selectedPlaylist ? (
                /* ════ DETAIL VIEW ════ */
                <div className="el-detail-root">
                    <button className="el-back-btn" onClick={() => { setSelectedPlaylist(null); setPlaylistDetail(null); setPlayingVideo(null); }}>
                        ← Back to Library
                    </button>

                    {/* Subject banner */}
                    <div className="el-subject-banner">
                        <div>
                            <h2 className="el-subject-title">{selectedPlaylist.name}</h2>
                            <div className="el-subject-meta">
                                <span>🏛️ {selectedPlaylist.department}</span>
                                <span>📖 {selectedPlaylist.subject}</span>
                                {selectedPlaylist.teacher_name && <span>👨‍🏫 {selectedPlaylist.teacher_name}</span>}
                            </div>
                        </div>
                        <div className="el-subject-counts">
                            <span className="el-badge el-badge-green">🎥 {selectedPlaylist.video_count || 0} Videos</span>
                            <span className="el-badge el-badge-blue">📝 {selectedPlaylist.note_count || 0} Notes</span>
                        </div>
                    </div>

                    {detailLoading ? (
                        <div className="el-spinner-wrap"><div className="el-spinner" />Loading content…</div>
                    ) : playlistDetail ? (
                        <div className="el-detail-body">
                            {/* Left — player + video list */}
                            <div className="el-player-col">
                                {playingVideo ? (
                                    <div className="el-player-card">
                                        <VideoPlayer video={playingVideo} />
                                        <div className="el-player-info">
                                            <h3 className="el-player-title">{playingVideo.title}</h3>
                                            {playingVideo.description && <p className="el-player-desc">{playingVideo.description}</p>}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="el-player-placeholder">🎬 Select a video to start watching</div>
                                )}

                                {/* Tabs */}
                                <div className="el-tabs">
                                    <button className={`el-tab ${activeTab === 'videos' ? 'el-tab-active' : ''}`} onClick={() => setActiveTab('videos')}>
                                        🎥 Videos ({playlistDetail.videos?.length || 0})
                                    </button>
                                    <button className={`el-tab ${activeTab === 'notes' ? 'el-tab-active' : ''}`} onClick={() => setActiveTab('notes')}>
                                        📝 Notes ({playlistDetail.notes?.length || 0})
                                    </button>
                                </div>

                                {activeTab === 'videos' && (
                                    <div className="el-video-list">
                                        {(playlistDetail.videos || []).length === 0
                                            ? <div className="el-empty">No videos available yet.</div>
                                            : playlistDetail.videos.map((v, i) => {
                                                const isPlaying = playingVideo?.id === v.id;
                                                const ytId = getYouTubeId(v.youtube_link || v.video_path);
                                                return (
                                                    <div key={v.id} className={`el-vitem ${isPlaying ? 'el-vitem-active' : ''}`} onClick={() => setPlayingVideo(v)}>
                                                        {ytId
                                                            ? <img className="el-thumb" src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt={v.title} />
                                                            : <div className="el-thumb-placeholder">{i + 1}</div>
                                                        }
                                                        <div className="el-vitem-info">
                                                            <div className="el-vitem-title">{v.title}</div>
                                                            {v.description && <div className="el-vitem-desc">{v.description}</div>}
                                                        </div>
                                                        <div className="el-vitem-icon">{isPlaying ? '🔊' : '▶'}</div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}

                                {activeTab === 'notes' && (
                                    <div className="el-notes-list">
                                        {(playlistDetail.notes || []).length === 0
                                            ? <div className="el-empty">No notes available yet.</div>
                                            : playlistDetail.notes.map(n => {
                                                const url = resolveNoteUrl(n);
                                                return (
                                                    <div key={n.id} className="el-note-card">
                                                        <div className="el-note-icon">📄</div>
                                                        <div className="el-note-info">
                                                            <div className="el-note-title">{n.title || n.file_name || 'Note'}</div>
                                                            <div className="el-note-date">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</div>
                                                        </div>
                                                        {url && (
                                                            <a href={url} target="_blank" rel="noreferrer" className="el-dl-btn">⬇ Download</a>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : (
                /* ════ LIBRARY GRID VIEW ════ */
                <div>
                    {/* Hero stats */}
                    <div className="el-hero">
                        <div>
                            <h1 className="el-hero-title">📚 E-Learning Library</h1>
                            <p className="el-hero-sub">Browse lectures, notes and materials from your university</p>
                        </div>
                        <div className="el-hero-stats">
                            <div className="el-stat"><span className="el-stat-num">{playlists.length}</span><span className="el-stat-label">Subjects</span></div>
                            <div className="el-stat"><span className="el-stat-num">{totalVideos}</span><span className="el-stat-label">Videos</span></div>
                            <div className="el-stat"><span className="el-stat-num">{totalNotes}</span><span className="el-stat-label">Notes</span></div>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="el-search-wrap">
                        <span className="el-search-icon">🔍</span>
                        <input
                            className="el-search"
                            placeholder="Search subjects, departments, teachers…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && <button className="el-clear-btn" onClick={() => setSearch('')}>✕</button>}
                    </div>

                    {loading ? (
                        <div className="el-spinner-wrap"><div className="el-spinner" />Loading library…</div>
                    ) : filtered.length === 0 ? (
                        <div className="el-empty-state">
                            <div className="el-empty-icon">📭</div>
                            <h3>{search ? 'No results found' : 'No Content Available'}</h3>
                            <p>{search ? `No subjects match "${search}"` : 'Check back later for new e-learning content.'}</p>
                        </div>
                    ) : (
                        <div className="el-departments">
                            {Object.entries(groupedPlaylists)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([dep, list]) => (
                                <div key={dep} className="el-dep-section">
                                    <div className="el-dep-header">
                                        <span className="el-dep-icon">🏛️</span>
                                        <h2 className="el-dep-title">{dep}</h2>
                                        <span className="el-dep-count">{list.length} Subjects</span>
                                    </div>
                                    <div className="el-grid">
                                        {list.map(p => (
                                            <div key={p.id} className="el-card" onClick={() => selectPlaylist(p)}>
                                                <div className="el-card-accent" />
                                                <div className="el-card-body">
                                                    <h3 className="el-card-title">{p.name}</h3>
                                                    <div className="el-card-tags">
                                                        {p.subject && p.subject !== p.name && <span className="el-tag el-tag-blue">{p.subject}</span>}
                                                    </div>
                                                    {p.teacher_name && <div className="el-card-teacher">👨‍🏫 {p.teacher_name}</div>}
                                                    <div className="el-card-counts">
                                                        <span>🎥 {p.video_count || 0} videos</span>
                                                        <span>📝 {p.note_count || 0} notes</span>
                                                    </div>
                                                </div>
                                                <div className="el-card-arrow">→</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Layout>
    );
};

/* ─── Inline styles object (for dynamic values only) ─────────────────────── */
const styles = {
    ytWrap: { position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' },
    ytFrame: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' },
    videoEl: { width: '100%', maxHeight: '420px', display: 'block', background: '#000' },
    noMedia: { padding: '3rem', textAlign: 'center', color: '#888', background: '#f5f5f5', borderRadius: '12px' },
};

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

/* Toast */
.el-toast { position:fixed; top:1.5rem; right:1.5rem; z-index:99999; padding:0.85rem 1.4rem; border-radius:10px; font-size:0.9rem; font-weight:600; box-shadow:0 4px 20px rgba(0,0,0,0.15); animation:elSlideIn 0.3s ease; }
.el-toast-info    { background:#1a1a2e; color:#fff; }
.el-toast-error   { background:#f44336; color:#fff; }
.el-toast-success { background:#238548; color:#fff; }
@keyframes elSlideIn { from { transform:translateX(120%); opacity:0; } to { transform:translateX(0); opacity:1; } }

/* Hero */
.el-hero { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:1rem; margin-bottom:2rem; padding:2rem; background:linear-gradient(135deg,#238548,#1a6b37); border-radius:16px; color:#fff; }
.el-hero-title { margin:0 0 0.3rem; font-size:2rem; font-weight:800; }
.el-hero-sub { margin:0; opacity:0.85; font-size:0.95rem; }
.el-hero-stats { display:flex; gap:1.5rem; }
.el-stat { text-align:center; background:rgba(255,255,255,0.15); padding:0.8rem 1.2rem; border-radius:12px; }
.el-stat-num { display:block; font-size:1.8rem; font-weight:800; }
.el-stat-label { font-size:0.75rem; opacity:0.85; text-transform:uppercase; letter-spacing:0.05em; }

/* Search */
.el-search-wrap { position:relative; margin-bottom:1.5rem; }
.el-search-icon { position:absolute; left:1rem; top:50%; transform:translateY(-50%); font-size:1rem; pointer-events:none; }
.el-search { width:100%; padding:0.85rem 1rem 0.85rem 2.8rem; border:2px solid #e0e0e0; border-radius:12px; font-size:0.97rem; outline:none; transition:border 0.2s; box-sizing:border-box; }
.el-search:focus { border-color:#238548; }
.el-clear-btn { position:absolute; right:0.8rem; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; color:#aaa; font-size:1.1rem; padding:0.3rem; }

/* Departments */
.el-dep-section { margin-bottom: 2.5rem; }
.el-dep-header { display: flex; align-items: center; gap: 0.8rem; margin-bottom: 1.2rem; padding-bottom: 0.6rem; border-bottom: 2px solid #f0f0f0; }
.el-dep-icon { font-size: 1.6rem; }
.el-dep-title { margin: 0; font-size: 1.3rem; font-weight: 700; color: #1a1a2e; flex: 1; }
.el-dep-count { font-size: 0.8rem; font-weight: 700; color: #238548; background: #e8f5e9; padding: 0.3rem 0.8rem; border-radius: 20px; }

/* Grid */
.el-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1.2rem; }

/* Card */
.el-card { background:#fff; border-radius:14px; overflow:hidden; box-shadow:0 2px 12px rgba(0,0,0,0.06); cursor:pointer; transition:all 0.25s; display:flex; align-items:stretch; border:2px solid transparent; }
.el-card:hover { transform:translateY(-4px); box-shadow:0 8px 28px rgba(35,133,72,0.18); border-color:#238548; }
.el-card-accent { width:5px; background:linear-gradient(180deg,#238548,#2196F3); flex-shrink:0; }
.el-card-body { flex:1; padding:1.3rem; }
.el-card-title { margin:0 0 0.6rem; font-size:1.05rem; font-weight:700; color:#1a1a2e; line-height:1.3; }
.el-card-tags { display:flex; flex-wrap:wrap; gap:0.4rem; margin-bottom:0.5rem; }
.el-tag { padding:0.15rem 0.55rem; border-radius:20px; font-size:0.72rem; font-weight:600; }
.el-tag-purple { background:#ede7f6; color:#7b1fa2; }
.el-tag-blue   { background:#e3f2fd; color:#1565c0; }
.el-card-teacher { font-size:0.82rem; color:#888; margin-bottom:0.6rem; }
.el-card-counts { font-size:0.83rem; color:#238548; font-weight:600; display:flex; gap:1rem; }
.el-card-arrow { display:flex; align-items:center; padding:0 1rem; color:#ccc; font-size:1.3rem; transition:all 0.2s; }
.el-card:hover .el-card-arrow { color:#238548; }

/* Empty */
.el-empty-state { text-align:center; padding:4rem 2rem; color:#888; }
.el-empty-icon { font-size:4rem; margin-bottom:1rem; }
.el-empty-state h3 { margin:0 0 0.5rem; color:#555; font-size:1.3rem; }
.el-empty { text-align:center; padding:2rem; color:#aaa; background:#fafafa; border-radius:10px; margin:0.5rem 0; }

/* Spinner */
.el-spinner-wrap { display:flex; flex-direction:column; align-items:center; gap:0.8rem; padding:3rem; color:#888; }
.el-spinner { width:40px; height:40px; border:4px solid #e0e0e0; border-top-color:#238548; border-radius:50%; animation:spin 0.8s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

/* Detail */
.el-back-btn { background:#f0f4f8; border:none; border-radius:8px; padding:0.6rem 1.2rem; font-size:0.9rem; font-weight:600; color:#444; cursor:pointer; margin-bottom:1.2rem; transition:background 0.2s; }
.el-back-btn:hover { background:#e0e7ef; }
.el-subject-banner { background:linear-gradient(135deg,#1a1a2e,#2c3e6e); color:#fff; border-radius:14px; padding:1.5rem 2rem; margin-bottom:1.5rem; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:1rem; }
.el-subject-title { margin:0 0 0.4rem; font-size:1.5rem; font-weight:700; }
.el-subject-meta { display:flex; gap:1rem; flex-wrap:wrap; font-size:0.85rem; opacity:0.8; }
.el-subject-counts { display:flex; gap:0.6rem; }
.el-badge { padding:0.4rem 0.9rem; border-radius:20px; font-size:0.8rem; font-weight:700; }
.el-badge-green { background:rgba(35,133,72,0.25); border:1px solid rgba(35,133,72,0.5); color:#5ef59a; }
.el-badge-blue  { background:rgba(33,150,243,0.25); border:1px solid rgba(33,150,243,0.5); color:#90caf9; }

.el-detail-body { display:flex; flex-direction:column; gap:1.2rem; }
.el-player-col  { display:flex; flex-direction:column; gap:1rem; }

/* Player */
.el-player-card { border-radius:14px; overflow:hidden; box-shadow:0 4px 20px rgba(0,0,0,0.12); background:#000; }
.el-player-info { padding:1rem 1.2rem; background:#fff; }
.el-player-title { margin:0 0 0.25rem; font-size:1.1rem; font-weight:700; color:#1a1a2e; }
.el-player-desc  { margin:0; font-size:0.85rem; color:#888; }
.el-player-placeholder { background:#f0f4f8; border-radius:14px; padding:4rem 2rem; text-align:center; color:#aaa; font-size:1.1rem; }

/* Tabs */
.el-tabs { display:flex; border-bottom:2px solid #eee; }
.el-tab { background:none; border:none; padding:0.75rem 1.2rem; font-size:0.9rem; font-weight:600; color:#888; cursor:pointer; border-bottom:3px solid transparent; margin-bottom:-2px; transition:all 0.2s; }
.el-tab-active { color:#238548; border-bottom-color:#238548; }

/* Video list */
.el-video-list { display:flex; flex-direction:column; gap:0.5rem; max-height:380px; overflow-y:auto; }
.el-vitem { display:flex; align-items:center; gap:0.8rem; padding:0.7rem; border-radius:10px; cursor:pointer; transition:background 0.15s; border:2px solid transparent; }
.el-vitem:hover { background:#f5faf7; }
.el-vitem-active { background:#e8f5e9; border-color:#238548; }
.el-thumb { width:72px; height:48px; border-radius:6px; object-fit:cover; flex-shrink:0; }
.el-thumb-placeholder { width:72px; height:48px; border-radius:6px; background:#e8f5e9; display:flex; align-items:center; justify-content:center; font-weight:700; color:#238548; flex-shrink:0; }
.el-vitem-info { flex:1; min-width:0; }
.el-vitem-title { font-weight:600; font-size:0.88rem; color:#1a1a2e; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.el-vitem-desc  { font-size:0.75rem; color:#aaa; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.el-vitem-icon  { font-size:0.85rem; color:#888; flex-shrink:0; }

/* Notes list */
.el-notes-list { display:flex; flex-direction:column; gap:0.6rem; }
.el-note-card { display:flex; align-items:center; gap:1rem; padding:1rem 1.2rem; background:#fff; border-radius:10px; border:1px solid #eee; box-shadow:0 1px 4px rgba(0,0,0,0.05); }
.el-note-icon  { font-size:1.5rem; flex-shrink:0; }
.el-note-info  { flex:1; }
.el-note-title { font-weight:600; font-size:0.9rem; color:#1a1a2e; }
.el-note-date  { font-size:0.75rem; color:#aaa; }
.el-dl-btn { background:#e8f5e9; color:#238548; text-decoration:none; padding:0.4rem 0.9rem; border-radius:8px; font-size:0.8rem; font-weight:600; white-space:nowrap; transition:background 0.2s; }
.el-dl-btn:hover { background:#c8e6c9; }
`;

export default StudentElearning;
